import React, { useCallback, useEffect, useState } from "react";
import slide1 from "../../Assets/slide 1.webp";
import slide2 from "../../Assets/slide 2.webp";
import slide3 from "../../Assets/slide 3.webp";
import mobile1 from "../../Assets/mobile1.webp";
import mobile2 from "../../Assets/mobile 2.webp";
import mobile3 from "../../Assets/mobile 3.webp";
import "./HeroBanner.css";

const ADMIN_URL = "https://admin.yogarefer.com";
const AUTOPLAY_MS = 4000;

const SLIDES = [
  {
    desktop: slide1,
    mobile: mobile1,
    title: "8.4 Million Yoga Poses & A Global Contribution!",
    text: "Join a worldwide mission to build the largest collection of yoga poses and variations, created by practitioners and shared with everyone.",
    cta: "Explore Yoga Poses",
    href: "#latest-variation-poses",
  },
  {
    desktop: slide2,
    mobile: mobile2,
    title: "Your Pose. Your Knowledge. Your Contribution.",
    text: "Upload new yoga pose variations and share your techniques with practitioners around the world.",
    cta: "Upload Your Yoga Pose",
    href: `${ADMIN_URL}/`,
    external: true,
  },
  {
    desktop: slide3,
    mobile: mobile3,
    title: "Unite Yoga Wisdom Across the World.",
    text: "Uploading a photo extends the validity of your logo and media link. Your yoga photo and name will receive lifetime free promotion.",
    cta: "Join the Global Contribution",
    href: `${ADMIN_URL}/register`,
    external: true,
  },
];

const HeroBanner = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback(
    (index) => setActive((index + SLIDES.length) % SLIDES.length),
    [],
  );

  // Autoplay; pauses while hovered/focused and for users who prefer reduced motion
  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (paused || reduceMotion) return undefined;
    const timer = setTimeout(() => goTo(active + 1), AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [active, paused, goTo]);

  const handleCtaClick = (e, slide) => {
    if (slide.href.startsWith("#")) {
      e.preventDefault();
      document
        .getElementById(slide.href.slice(1))
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      className="hero-banner"
      aria-roledescription="carousel"
      aria-label="YogaRefer highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {SLIDES.map((slide, i) => (
        <div
          key={slide.title}
          className={`hero-slide ${i === active ? "is-active" : ""}`}
          role="group"
          aria-roledescription="slide"
          aria-label={`${i + 1} of ${SLIDES.length}`}
          aria-hidden={i !== active}
        >
          <picture>
            <source media="(max-width: 767px)" srcSet={slide.mobile} />
            <img
              className="hero-image"
              src={slide.desktop}
              alt=""
              loading={i === 0 ? "eager" : "lazy"}
              fetchpriority={i === 0 ? "high" : undefined}
            />
          </picture>

          <div className="hero-overlay" />

          <div className="hero-content">
            <h1 className="hero-title">{slide.title}</h1>
            <p className="hero-text">{slide.text}</p>
            <a
              className="hero-cta"
              href={slide.href}
              tabIndex={i === active ? 0 : -1}
              onClick={(e) => handleCtaClick(e, slide)}
              {...(slide.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {slide.cta}
            </a>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="hero-arrow hero-arrow-prev"
        aria-label="Previous slide"
        onClick={() => goTo(active - 1)}
      >
        ‹
      </button>
      <button
        type="button"
        className="hero-arrow hero-arrow-next"
        aria-label="Next slide"
        onClick={() => goTo(active + 1)}
      >
        ›
      </button>

      <div className="hero-dots">
        {SLIDES.map((slide, i) => (
          <button
            key={slide.title}
            type="button"
            className={`hero-dot ${i === active ? "is-active" : ""}`}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === active}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroBanner;
